import { MonoBehavior } from 'game/core/monoBehavior';
import { Container, Graphics, Point, Sprite } from 'pixi.js';

import { Card } from './Card';
import { getCardAsset, getCardBackAsset } from 'constants/cardAssetsList';
import gsap, { Power1 } from 'gsap';
import { Layout } from 'game/LayoutManager';
import { ShadowContainer } from './ShadowContainer';
import { shakePositions } from 'game/helpers/shakeAnimation';
import { Store } from 'game/store/gameStore';
import { CARD_SHAKE_DURATION, FLIP_CARD_TIME, STOCK_PILE_FLIP_TIME } from 'constants/move';
import { GameEvent } from 'game/events/gameEvent';
import { EventName } from 'game/events/eventsName';
import { MagicCardParticles } from 'game/MagicParticles';
const INACTIVE_OVERLAY_ALPHA = 0.28;
export class CardDisplay extends MonoBehavior {
    card: Card;
    reverse: Container;
    reverseSprite?: Sprite;
    averse: Container;
    averseSprite?: Sprite;
    container: Container;
    shadowContainer: ShadowContainer;
    hasShadow = false;
    isShaking = false;
    shakeTime = -Infinity;
    shakeRandoms = [] as Point[];
    startShakeTime = -1000;
    listeners = [] as string[];
    inactiveOverlay: Graphics;
    blockTween?: gsap.core.Tween;

    constructor(card: Card) {
        super();
        this.card = card;
        this.redraw = this.redraw.bind(this);
        this.container = new Container();
        this.reverse = new Container();
        this.averse = new Container();
        this.inactiveOverlay = new Graphics();
        this.inactiveOverlay.alpha = 0;

        this.shadowContainer = new ShadowContainer(this.container);
        this.listeners.push(GameEvent.add(EventName.CHANGE_CARD_BACK, this.redraw));
        this.listeners.push(GameEvent.add(EventName.CHANGE_CARD_FRONT, this.redraw));
        this.draw();
    }
    draw() {
        this.container.addChild(this.reverse);
        this.container.addChild(this.averse);
        this.container.addChild(this.inactiveOverlay);

        this.card.container.addChild(this.container);
        this.averse.visible = false;

        this.redraw();
    }
    dealFromStockPile() {
        const duration = STOCK_PILE_FLIP_TIME;

        gsap.to(this.card.container.skew, { y: 0.3, duration: duration });
        gsap.to(this.card.container.scale, { x: 0, ease: Power1.easeIn, duration: duration }).then(() => {
            this.card.container.skew.set(0, -0.3);

            if (this.reverse) this.reverse.visible = false;
            if (this.averse) this.averse.visible = true;
            gsap.to(this.card.container.scale, { x: 1, ease: Power1.easeOut, duration: duration });
            gsap.to(this.card.container.skew, { y: 0, duration: duration });
        });
    }

    setVisible(show: boolean, noAnimation = false) {
        const duration = FLIP_CARD_TIME;
        if (noAnimation) {
            if (this.reverse) this.reverse.visible = !show;
            if (this.averse) this.averse.visible = show;
        } else {
            gsap.to(this.card.container.skew, { y: 0.3, duration: duration });
            gsap.to(this.card.container.scale, { x: 0, ease: Power1.easeIn, duration: duration }).then(() => {
                this.card.container.skew.set(0, -0.3);

                if (this.reverse) this.reverse.visible = !show;
                if (this.averse) this.averse.visible = show;
                gsap.to(this.card.container.scale, { x: 1, ease: Power1.easeOut, duration: duration });
                gsap.to(this.card.container.skew, { y: 0, duration: duration });
            });
        }
    }

    async redraw() {
        const txt = await getCardBackAsset(Store.back);

        const reverseSprite = new Sprite(txt);
        reverseSprite.anchor.set(0.5);
        const txtColor = await getCardAsset(Store.front, this.card.code, this.card.cardColor);
        const averseSprite = Sprite.from(txtColor);

        averseSprite.anchor.set(0.5);
        const cardSize = Layout.getCardSize();

        if (this.reverseSprite && this.reverseSprite.texture && reverseSprite.texture)
            this.reverseSprite.texture = reverseSprite.texture;
        else {
            this.reverseSprite = reverseSprite;
            this.reverse.addChild(this.reverseSprite);
        }
        if (this.averseSprite && this.averseSprite.texture && averseSprite.texture)
            this.averseSprite.texture = averseSprite.texture;
        else {
            this.averseSprite = averseSprite;
            this.averse.addChild(this.averseSprite);
        }
        this.averseSprite.width = cardSize.width;
        this.reverseSprite.width = cardSize.width;
        this.averseSprite.height = cardSize.height;
        this.reverseSprite.height = cardSize.height;
        this.inactiveOverlay?.clear?.();

        this.inactiveOverlay.roundRect(
            -cardSize.width / 2,
            -cardSize.height / 2,
            cardSize.width,
            cardSize.height,
            cardSize.width * 0.08
        );
        this.inactiveOverlay.fill(0x000000);
    }

    castShadow(hasShadow: boolean) {
        if (hasShadow === this.hasShadow) return;

        this.hasShadow = hasShadow;
        this.shadowContainer.visible(this.hasShadow);
    }

    // Method to shake the cards
    shakeCard() {
        this.isShaking = true;
        this.startShakeTime = performance.now();
    }
    update(): void {
        if (this.isShaking) {
            const shakeFrame = Math.floor(
                ((performance.now() - this.startShakeTime) / CARD_SHAKE_DURATION) * shakePositions.length
            );
            if (shakeFrame < shakePositions.length) {
                const point = shakePositions[shakeFrame];
                this.container.position = point;
            } else {
                this.isShaking = false;
                this.container.position.set(0, 0);
            }
        }
    }
    resize(): void {
        this.redraw();
    }

    destroy(): void {
        super.destroy();
        this.listeners.forEach((l) => GameEvent.remove(l));
        this.container.destroy({ children: true });
    }
    blockOverlayPop() {
        if (this.blockTween) this.blockTween.kill();
        this.blockTween = gsap.to(this.inactiveOverlay, { alpha: INACTIVE_OVERLAY_ALPHA, duration: 0.2 });
    }
    hideOverlay() {
        this.showInactiveOverlay(false);
    }

    showInactiveOverlay(fade: boolean) {
        if (this.blockTween) this.blockTween.kill();
        this.blockTween = gsap.to(this.inactiveOverlay, { alpha: fade ? INACTIVE_OVERLAY_ALPHA : 0, duration: 0.2 });
    }

    showMagic() {
        new MagicCardParticles(this.card, 1);
    }
    glowMagic() {
        const { height, width } = Layout.getCardSize();
        for (let y = 0; y < 5; y++) {
            const posRight = new Point(width * 0.48, -height / 2 + (height / 4) * y);
            new MagicCardParticles(this.card, 12, posRight, 950);

            const posLeft = new Point(-width * 0.48, -height / 2 + (height / 4) * y);
            new MagicCardParticles(this.card, 12, posLeft, 950);
        }
    }
}
